import React, { useState, useCallback, useRef } from 'react';
import { Play, RotateCcw, CheckCircle, XCircle, Clock, MemoryStick, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// 直接导入 Monaco Editor，避免生成 chunk 文件
import Editor from '@monaco-editor/react';

interface CppCodeEditorProps {
  initialCode?: string;
  starterCode?: string;
  onRun?: (code: string) => void;
  onSubmit?: (code: string) => void;
  isRunning?: boolean;
  isSubmitting?: boolean;
  output?: string;
  error?: string;
  executionTime?: number;
  compileTime?: number;
  testResults?: {
    passed: boolean;
    total: number;
    details: Array<{
      testCaseId: string;
      passed: boolean;
      actualOutput: string;
      expectedOutput: string;
      error?: string;
    }>;
  };
  readOnly?: boolean;
  height?: string;
  submitLabel?: string;
}

export default function CppCodeEditor({
  initialCode = '',
  starterCode = '',
  onRun,
  onSubmit,
  isRunning = false,
  isSubmitting = false,
  output = '',
  error = '',
  executionTime = 0,
  compileTime = 0,
  testResults,
  readOnly = false,
  height = '400px',
  submitLabel = '保存并提交',
}: CppCodeEditorProps) {
  const [code, setCode] = useState(initialCode || starterCode);
  const editorRef = useRef<any>(null);
  const [confirmAction, setConfirmAction] = useState<'reset' | 'clear' | null>(null);

  const handleReset = useCallback(() => {
    setCode(starterCode);
    if (editorRef.current) {
      editorRef.current.setValue(starterCode);
    }
    setConfirmAction(null);
  }, [starterCode]);

  const handleClear = useCallback(() => {
    setCode('');
    if (editorRef.current) {
      editorRef.current.setValue('');
    }
    setConfirmAction(null);
  }, []);

  const requestReset = useCallback(() => {
    setConfirmAction('reset');
  }, []);

  const requestClear = useCallback(() => {
    setConfirmAction('clear');
  }, []);

  const cancelConfirm = useCallback(() => {
    setConfirmAction(null);
  }, []);

  const handleRun = useCallback(() => {
    onRun?.(code);
  }, [code, onRun]);

  const handleSubmit = useCallback(() => {
    onSubmit?.(code);
  }, [code, onSubmit]);

  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header - 与 Python 编辑器一致的风格 */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">C++</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">C++ 代码编辑器</h3>
              <p className="text-xs text-gray-500">支持 C++17/GCC 编译器</p>
            </div>
          </div>
          <div className="flex items-center gap-2 relative">
            <button
              onClick={handleRun}
              disabled={isRunning || isSubmitting || readOnly}
              className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              {isRunning ? '运行中...' : '运行测试'}
            </button>
            <button
              onClick={requestReset}
              disabled={readOnly}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              重置代码
            </button>
            <button
              onClick={requestClear}
              disabled={readOnly}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              清空
            </button>
            <AnimatePresence>
              {confirmAction && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-4 min-w-[220px]"
                >
                  <p className="text-sm text-gray-700 mb-3">
                    {confirmAction === 'reset'
                      ? '确定要将代码恢复到初始状态吗？'
                      : '确定要清空所有代码吗？'}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={confirmAction === 'reset' ? handleReset : handleClear}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${
                        confirmAction === 'clear'
                          ? 'bg-red-50 text-red-600 hover:bg-red-100'
                          : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                      }`}
                    >
                      {confirmAction === 'reset' ? <RotateCcw className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
                      确定
                    </button>
                    <button
                      onClick={cancelConfirm}
                      className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      取消
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Editor - 使用 Monaco Editor */}
      <div className="border-b border-gray-200">
          <Editor
            height={height}
            language="cpp"
            theme="vs-dark"
            value={code}
            onChange={(value) => setCode(value || '')}
            onMount={handleEditorDidMount}
            options={{
              fontSize: 14,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
              insertSpaces: true,
              readOnly: readOnly,
            }}
          />
      </div>

      {/* Output Area - 与 Python 编辑器一致 */}
      <div className="bg-gray-50">
        <div className="p-4 min-h-[200px]">
          <AnimatePresence mode="wait">
            {testResults ? (
              <motion.div
                key="testcases"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-3"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-medium text-gray-700">测试结果</span>
                  <span className="px-2 py-0.5 text-xs bg-gray-100 rounded-full">
                    {testResults.passed}/{testResults.total}
                  </span>
                </div>
                {testResults.details.map((result, index) => (
                  <div
                    key={result.testCaseId}
                    className={`border rounded-lg p-3 ${
                      result.passed
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {result.passed ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600" />
                      )}
                      <span className="font-medium text-sm">
                        测试用例 {index + 1}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          result.passed
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {result.passed ? '通过' : '失败'}
                      </span>
                    </div>

                    {!result.passed && (
                      <div className="space-y-2 text-xs">
                        <div>
                          <span className="text-gray-500">期望输出:</span>
                          <pre className="mt-1 p-2 bg-white rounded border border-gray-200 font-mono whitespace-pre-wrap">
                            {result.expectedOutput}
                          </pre>
                        </div>
                        <div>
                          <span className="text-gray-500">实际输出:</span>
                          <pre className="mt-1 p-2 bg-white rounded border border-gray-200 font-mono whitespace-pre-wrap">
                            {result.actualOutput || result.error || '无输出'}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </motion.div>
            ) : (
              <motion.div
                key="output"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                {isRunning ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">正在编译执行...</span>
                  </div>
                ) : (
                  <>
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-red-700 mb-2">
                          <XCircle className="w-5 h-5" />
                          <span className="font-medium">编译/执行错误</span>
                        </div>
                        <pre className="text-sm text-red-600 whitespace-pre-wrap font-mono overflow-auto max-h-40">
                          {error}
                        </pre>
                      </div>
                    )}

                    {output && !error && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 text-green-700 mb-2">
                          <CheckCircle className="w-5 h-5" />
                          <span className="font-medium">执行成功</span>
                        </div>
                        <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono overflow-auto max-h-40">
                          {output}
                        </pre>
                      </div>
                    )}

                    {(executionTime > 0 || compileTime > 0) && (
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        {compileTime > 0 && (
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4" />
                            <span>编译时间: {compileTime}ms</span>
                          </div>
                        )}
                        {executionTime > 0 && (
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4" />
                            <span>执行时间: {executionTime}ms</span>
                          </div>
                        )}
                      </div>
                    )}

                    {!output && !error && !isRunning && (
                      <div className="text-center py-8 text-gray-400">
                        <Play className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p>点击运行测试查看输出结果</p>
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer - 与 Python 编辑器一致 */}
      <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-600">
          {testResults && (
            <div className="flex items-center gap-2">
              <span>测试通过:</span>
              <span className={`font-bold ${
                Number(testResults.passed) === testResults.total
                  ? 'text-green-600'
                  : 'text-orange-600'
              }`}>
                {testResults.passed}/{testResults.total}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {onSubmit && (
            <button
              onClick={handleSubmit}
              disabled={isRunning || isSubmitting || readOnly}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="w-4 h-4" />
              {isSubmitting ? '保存中...' : submitLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
